// src/components/PriorAuthLetterPanel.tsx
// ──────────────────────────────────────────────────────────────
// Prior Authorization Letter Generator panel for the case detail page.
// Generates clinical arguments via Claude (de-identified), then
// assembles the full letter client-side with patient details.
// ──────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "../lib/supabase";
import {
  assembleLetter,
  getTemplatesForRequestType,
  LETTER_TEMPLATES,
  type LetterSlots,
} from "../utils/letterTemplates";
import { LetterEditor } from "./LetterEditor";
import { generateLetterPDF, getLetterFilename } from "../utils/letterPDF";
import { normalizeInsurer } from "../utils/submissionValidation";
import { redactLetterPHI, hydrateLetterPHI } from "../utils/letterRedaction";
import { getCaseDisplayName } from "../utils/caseDisplayName";
import "../styles/letterEditor.css";

// ── Types ────────────────────────────────────────────────────

interface ClinicalArgument {
  clinical_justification: string;
  criteria_citations: string[];
  literature_references: string[];
  risks_of_denial: string[];
  recommended_additions: string[];
}

interface LetterRecord {
  id: string;
  version: number;
  clinical_argument: ClinicalArgument;
  generation_params: Record<string, any>;
  letter_template: string;
  letter_content?: string; // Redacted letter text (PHI stripped)
  status: string;
  created_at: string;
}

interface PhysicianProfile {
  physician_name?: string;
  npi_number?: string;
  practice_name?: string;
  practice_address?: string;
  phone_number?: string;
  fax_number?: string;
  specialty?: string;
}

interface PriorAuthLetterPanelProps {
  caseData: {
    id: string;
    friendly_name?: string;
    request_type?: string;
    request_details?: string;
    insurance_company?: string;
    plan_type?: string;
    diagnoses?: string;
    denial_reason?: string;
    case_status?: string;
    tried_already?: string;
    additional_context?: string;
  };
  /** When true, auto-expand and generate an appeal letter */
  triggerAppeal?: boolean;
  /** Called after appeal letter is generated so parent can update status */
  onAppealGenerated?: () => void;
  /** Called whenever a clinical argument is loaded or generated (for P2P bridge) */
  onClinicalArgumentReady?: (arg: ClinicalArgument) => void;
}

type PanelState = "empty" | "generating" | "preview" | "editing" | "error";

// ── Icons ────────────────────────────────────────────────────

function IconDocument({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

function IconSparkles({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  );
}

function IconCopy({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
      />
    </svg>
  );
}

function IconRefresh({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182"
      />
    </svg>
  );
}

function IconPencil({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

function IconCheck({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function IconChevronDown({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function IconDownload({ className = "w-4 h-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
      />
    </svg>
  );
}

// ── Component ────────────────────────────────────────────────

export function PriorAuthLetterPanel({
  caseData,
  triggerAppeal,
  onAppealGenerated,
  onClinicalArgumentReady,
}: PriorAuthLetterPanelProps) {
  const { user } = useUser();
  const [panelState, setPanelState] = useState<PanelState>("empty");
  const [letterRecord, setLetterRecord] = useState<LetterRecord | null>(null);
  const [assembledLetter, setAssembledLetter] = useState("");
  const [editedLetter, setEditedLetter] = useState("");
  const [profile, setProfile] = useState<PhysicianProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("standard_v1");
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const editorRef = useRef<{
    getHTML: () => string;
    getText: () => string;
  } | null>(null);

  const userId = user?.id;

  // Determine request type for template selection
  const requestType =
    caseData.case_status === "denied" || caseData.case_status === "appealed"
      ? "appeal"
      : "initial";

  // Load physician profile
  useEffect(() => {
    if (!userId) return;
    supabase
      .from("physician_profiles")
      .select(
        "physician_name, npi_number, practice_name, practice_address, phone_number, fax_number, specialty"
      )
      .eq("user_id", userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[LetterPanel] Profile fetch failed:", error.message);
        }
        if (data) setProfile(data);
        setProfileLoaded(true);
      });
  }, [userId]);

  // Check for existing letter on mount
  // Step 1: Fetch the letter record from DB
  const [pendingRecord, setPendingRecord] = useState<LetterRecord | null>(null);
  const [recordFetched, setRecordFetched] = useState(false);

  useEffect(() => {
    if (!userId || !caseData.id) {
      console.log(
        "[LetterPanel] Skipping fetch — userId:",
        userId,
        "caseId:",
        caseData.id
      );
      return;
    }

    console.log(
      "[LetterPanel] Fetching letter for case:",
      caseData.id,
      "user:",
      userId
    );

    supabase
      .from("prior_auth_letters")
      .select(
        "id, version, clinical_argument, generation_params, letter_template, letter_content, status, created_at"
      )
      .eq("case_id", caseData.id)
      .eq("user_id", userId)
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        console.log(
          "[LetterPanel] Fetch result — data:",
          data
            ? `v${data.version}, has_content=${!!data.letter_content}`
            : "null",
          "error:",
          error
        );
        if (error) {
          console.error("[LetterPanel] Fetch error:", error);
        }
        if (data) {
          setPendingRecord(data as LetterRecord);
        }
        setRecordFetched(true);
      });
  }, [userId, caseData.id]);

  // Step 2: Assemble the letter once we have the record
  // Path A: letter_content exists → hydrate immediately (no profile needed)
  // Path B: no content, profile loaded → reassemble with profile
  // Path C: no content, profile failed → reassemble without profile (placeholder data)
  useEffect(() => {
    console.log(
      "[LetterPanel] Phase 2 — pendingRecord:",
      pendingRecord?.id || "null",
      "profile:",
      !!profile,
      "profileLoaded:",
      profileLoaded,
      "panelState:",
      panelState
    );
    if (!pendingRecord) return;
    // Already loaded into state — skip
    if (letterRecord?.id === pendingRecord.id && panelState === "preview")
      return;

    if (pendingRecord.letter_content) {
      // Path A: Stored letter exists — hydrate PHI from local data (no profile needed)
      const letter = hydrateLetterPHI(pendingRecord.letter_content, {
        patient_name: "[Patient Name]",
      });

      setLetterRecord(pendingRecord);
      setSelectedTemplate(pendingRecord.letter_template);
      setAssembledLetter(letter);
      setEditedLetter(letter);
      setPanelState("preview");

      if (pendingRecord.clinical_argument.recommended_additions?.length) {
        setRecommendations(
          pendingRecord.clinical_argument.recommended_additions
        );
      }
      onClinicalArgumentReady?.(pendingRecord.clinical_argument);
    } else if (profile) {
      // Path B: No stored content — reassemble from clinical argument with profile
      const letter = buildLetter(
        pendingRecord.clinical_argument,
        pendingRecord.letter_template
      );

      setLetterRecord(pendingRecord);
      setSelectedTemplate(pendingRecord.letter_template);
      setAssembledLetter(letter);
      setEditedLetter(letter);
      setPanelState("preview");

      if (pendingRecord.clinical_argument.recommended_additions?.length) {
        setRecommendations(
          pendingRecord.clinical_argument.recommended_additions
        );
      }
      onClinicalArgumentReady?.(pendingRecord.clinical_argument);

      // Now that we've assembled with profile, save the content for next time
      saveLetterContent(pendingRecord.id, letter);
    } else if (profileLoaded && !profile) {
      // Path C: Profile fetch completed but failed — assemble without profile
      // Letter will have placeholder physician info; user can edit
      console.warn(
        "[LetterPanel] Assembling letter without profile (fetch failed)"
      );
      const letter = buildLetter(
        pendingRecord.clinical_argument,
        pendingRecord.letter_template
      );

      setLetterRecord(pendingRecord);
      setSelectedTemplate(pendingRecord.letter_template);
      setAssembledLetter(letter);
      setEditedLetter(letter);
      setPanelState("preview");

      if (pendingRecord.clinical_argument.recommended_additions?.length) {
        setRecommendations(
          pendingRecord.clinical_argument.recommended_additions
        );
      }
      onClinicalArgumentReady?.(pendingRecord.clinical_argument);
    }
    // If profile fetch hasn't completed yet, wait — this effect
    // will re-run when profileLoaded changes.
  }, [pendingRecord, profile, profileLoaded]);

  // Auto-trigger appeal generation when parent requests it
  const [pendingAppealGenerate, setPendingAppealGenerate] = useState(false);
  const appealTriggeredRef = useRef(false);

  useEffect(() => {
    if (triggerAppeal && !appealTriggeredRef.current) {
      appealTriggeredRef.current = true;
      // Switch to appeal template and expand — generation fires in next effect
      setSelectedTemplate("appeal_v1");
      setIsExpanded(true);
      setPendingAppealGenerate(true);
    }
  }, [triggerAppeal]);

  // Fire generation after template state has settled
  useEffect(() => {
    if (pendingAppealGenerate && userId && selectedTemplate === "appeal_v1") {
      setPendingAppealGenerate(false);
      handleGenerate();
    }
  }, [pendingAppealGenerate, userId, selectedTemplate]);

  // Build assembled letter from clinical argument + local data
  const buildLetter = useCallback(
    (clinicalArg: ClinicalArgument, templateId: string): string => {
      // Fallback diagnosis: use request details context if no diagnosis entered
      const diagnosisDisplay =
        caseData.diagnoses?.trim() ||
        (caseData.request_details
          ? `Per clinical indication — ${caseData.request_details}`
          : undefined);

      const slots: LetterSlots = {
        practice_name: profile?.practice_name,
        practice_address: profile?.practice_address,
        practice_phone: profile?.phone_number,
        practice_fax: profile?.fax_number,
        physician_name: profile?.physician_name,
        physician_npi: profile?.npi_number,
        physician_specialty: profile?.specialty,
        patient_name: "[Patient Name]",
        insurance_company: normalizeInsurer(caseData.insurance_company),
        plan_type: caseData.plan_type,
        diagnosis_display: diagnosisDisplay,
        procedure_display: caseData.request_details,
        denial_reason: caseData.denial_reason,
        clinical_justification: clinicalArg.clinical_justification,
        criteria_citations: clinicalArg.criteria_citations,
        literature_references: clinicalArg.literature_references,
        risks_of_denial: clinicalArg.risks_of_denial,
        date_generated: new Date().toISOString(),
      };

      return assembleLetter(templateId, slots);
    },
    [caseData, profile]
  );

  // Build redaction context from case data
  const redactionContext = {
    patient_name: "[Patient Name]",
    // DOB and member_id are not collected in the case,
    // but handle them in case they're added in the future
    patient_dob: undefined as string | undefined,
    member_id: undefined as string | undefined,
  };

  // Save redacted letter content to DB
  const saveLetterContent = useCallback(
    async (letterId: string, letterText: string) => {
      const redacted = redactLetterPHI(letterText, redactionContext);
      console.log(
        "[LetterPanel] Saving letter content — id:",
        letterId,
        "length:",
        redacted.length
      );
      const { error } = await supabase
        .from("prior_auth_letters")
        .update({ letter_content: redacted })
        .eq("id", letterId);
      if (error) {
        console.error("[LetterPanel] Failed to save letter content:", error);
      } else {
        console.log("[LetterPanel] Letter content saved successfully");
      }
    },
    [redactionContext]
  );

  // Parse diagnosis codes from case data
  const parseDiagnosisCodes = (): {
    codes: string[];
    descriptions: string[];
  } => {
    const raw = caseData.diagnoses || "";
    const parts = raw
      .split(/[;,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const codes: string[] = [];
    const descriptions: string[] = [];

    for (const part of parts) {
      // Try to extract ICD-10 code pattern
      const match = part.match(/^([A-Z]\d{2}\.?\d{0,4})\s*[-—:]\s*(.+)/i);
      if (match) {
        codes.push(match[1]);
        descriptions.push(match[2].trim());
      } else if (/^[A-Z]\d{2}/i.test(part)) {
        codes.push(part);
      } else {
        descriptions.push(part);
      }
    }

    // If no codes found, use descriptions as-is
    if (codes.length === 0 && descriptions.length > 0) {
      return { codes: descriptions, descriptions };
    }

    return { codes, descriptions };
  };

  // Generate letter via edge function
  const handleGenerate = async () => {
    if (!userId) {
      console.error("[LetterPanel] Cannot generate — user not loaded");
      setErrorMsg("Unable to generate: please wait a moment and try again.");
      setPanelState("error");
      return;
    }

    if (!caseData.id) {
      console.error("[LetterPanel] Cannot generate — no case ID");
      setErrorMsg("Unable to generate: case data is not loaded yet.");
      setPanelState("error");
      return;
    }

    console.log("[LetterPanel] Starting generation for case:", caseData.id);
    setPanelState("generating");
    setIsExpanded(true);
    setErrorMsg("");

    try {
      const { codes, descriptions } = parseDiagnosisCodes();

      const resp = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/generate-prior-auth-letter`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            case_id: caseData.id,
            user_id: userId,
            diagnosis_codes: codes,
            diagnosis_descriptions: descriptions,
            procedure_description: caseData.request_details,
            insurance_company:
              normalizeInsurer(caseData.insurance_company) ||
              caseData.insurance_company,
            plan_type: caseData.plan_type,
            request_type: requestType,
            denial_reason: caseData.denial_reason,
            conservative_treatments: caseData.tried_already,
            clinical_notes: caseData.additional_context,
            template: selectedTemplate,
          }),
        }
      );

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Generation failed");

      const record: LetterRecord = {
        id: data.letter_id,
        version: data.version,
        clinical_argument: data.clinical_argument,
        generation_params: data.generation_params,
        letter_template: selectedTemplate,
        status: "draft",
        created_at: data.created_at,
      };

      setLetterRecord(record);
      const letter = buildLetter(data.clinical_argument, selectedTemplate);
      setAssembledLetter(letter);
      setEditedLetter(letter);
      setPanelState("preview");
      setIsExpanded(true);

      // Save redacted letter content to DB
      saveLetterContent(data.letter_id, letter);

      if (data.clinical_argument.recommended_additions?.length) {
        setRecommendations(data.clinical_argument.recommended_additions);
      }

      // Notify parent of new clinical argument (for P2P bridge)
      onClinicalArgumentReady?.(data.clinical_argument);

      // If this was an appeal generation, notify parent to update status
      if (requestType === "appeal") {
        onAppealGenerated?.();
      }
    } catch (e: any) {
      console.error("[PriorAuth] Generation error:", e);
      setErrorMsg(e.message || "Failed to generate letter");
      setPanelState("error");
    }
  };

  // Copy to clipboard — plain text from editor or assembled letter
  const handleCopy = async () => {
    const textToCopy = editorRef.current
      ? editorRef.current.getText()
      : assembledLetter;
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download as PDF
  const handleDownloadPDF = () => {
    const textContent = editorRef.current
      ? editorRef.current.getText()
      : assembledLetter;
    if (!textContent) return;

    const filename = getLetterFilename(
      getCaseDisplayName(caseData.request_details, caseData.diagnoses),
      caseData.request_type,
      requestType === "appeal"
    );
    generateLetterPDF(textContent, { filename, download: true });
  };

  // Toggle edit mode
  const handleEdit = () => {
    if (panelState === "editing") {
      // Save edits from rich text editor
      if (editorRef.current) {
        const html = editorRef.current.getHTML();
        const text = editorRef.current.getText();
        setEditedLetter(html);
        setAssembledLetter(text);

        // Persist redacted edits to DB
        if (letterRecord) {
          saveLetterContent(letterRecord.id, text);
        }
      }
      setPanelState("preview");
    } else {
      setPanelState("editing");
    }
  };

  // Switch template and reassemble
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    if (letterRecord) {
      const letter = buildLetter(letterRecord.clinical_argument, templateId);
      setAssembledLetter(letter);
      setEditedLetter(letter);

      // Persist updated template content to DB
      saveLetterContent(letterRecord.id, letter);

      // Also update the template reference
      supabase
        .from("prior_auth_letters")
        .update({ letter_template: templateId })
        .eq("id", letterRecord.id)
        .then(({ error }) => {
          if (error)
            console.error("[LetterPanel] Failed to update template:", error);
        });
    }
  };

  // Available templates based on request type
  const availableTemplates = getTemplatesForRequestType(requestType);

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center gap-3 px-5 py-[18px] text-left group transition-colors duration-150 hover:bg-slate-50 ${
          isExpanded ? "border-b border-slate-100" : ""
        }`}
      >
        <IconDocument className="w-3.5 h-3.5 text-slate-400 opacity-90 flex-shrink-0" />
        <span className="text-sm font-medium text-slate-700 flex-1 min-w-0">
          {requestType === "appeal" ? "Appeal Letter" : "Prior Auth Letter"}
        </span>
        {panelState === "preview" && (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
            <span className="text-[10px] font-medium text-teal-600">Ready</span>
          </>
        )}
        <IconChevronDown
          className={`w-3.5 h-3.5 text-slate-400 group-hover:text-slate-500 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expandable content */}
      <div
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-5 py-4">
          {/* Empty state — generate button */}
          {panelState === "empty" && (
            <div className="space-y-4">
              {/* Template selector */}
              {availableTemplates.length > 1 && (
                <div>
                  <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                    Letter Type
                  </label>
                  <div className="flex gap-2">
                    {availableTemplates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTemplate(t.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          selectedTemplate === t.id
                            ? "bg-teal-50 text-teal-700 border-2 border-teal-200"
                            : "bg-slate-50 text-slate-600 border border-slate-200 hover:border-teal-200"
                        }`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation check */}
              {(!caseData.diagnoses || !caseData.insurance_company) && (
                <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  <span className="font-semibold">Missing info: </span>
                  {!caseData.diagnoses && "diagnosis, "}
                  {!caseData.insurance_company && "insurance company"} — add
                  these to the case for the best results.
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={!caseData.diagnoses && !caseData.request_details}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 shadow-md shadow-teal-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <IconSparkles className="w-4 h-4" />
                {requestType === "appeal"
                  ? "Generate Appeal Letter"
                  : "Generate Prior Auth Letter"}
              </button>

              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                AI generates the clinical argument using de-identified case
                data. Patient details are added locally and never leave your
                device.
              </p>
            </div>
          )}

          {/* Generating state */}
          {panelState === "generating" && (
            <div className="py-8 text-center">
              <div
                className="animate-spin w-8 h-8 border-[3px] border-teal-500 border-t-transparent rounded-full mx-auto mb-4"
                style={{ borderTopColor: "transparent" }}
              />
              <p className="text-sm font-medium text-slate-700 mb-1">
                Generating clinical argument...
              </p>
              <p className="text-xs text-slate-400">
                Analyzing guidelines and building medical necessity case
              </p>
            </div>
          )}

          {/* Error state */}
          {panelState === "error" && (
            <div className="space-y-3">
              <div className="px-3 py-2.5 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
                {errorMsg}
              </div>
              <button
                onClick={handleGenerate}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                <IconRefresh className="w-4 h-4" />
                Try Again
              </button>
            </div>
          )}

          {/* Preview / Edit state */}
          {(panelState === "preview" || panelState === "editing") && (
            <div className="space-y-4">
              {/* Action bar */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  {copied ? (
                    <>
                      <IconCheck className="w-3.5 h-3.5 text-green-500" />
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <IconCopy className="w-3.5 h-3.5" />
                      Copy to Clipboard
                    </>
                  )}
                </button>

                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  <IconDownload className="w-3.5 h-3.5" />
                  Download PDF
                </button>

                <button
                  onClick={handleEdit}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    panelState === "editing"
                      ? "bg-teal-50 text-teal-700 border border-teal-200"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {panelState === "editing" ? (
                    <>
                      <IconCheck className="w-3.5 h-3.5" />
                      Done Editing
                    </>
                  ) : (
                    <>
                      <IconPencil className="w-3.5 h-3.5" />
                      Edit
                    </>
                  )}
                </button>

                <button
                  onClick={handleGenerate}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  <IconRefresh className="w-3.5 h-3.5" />
                  Regenerate
                </button>

                {/* Template switcher (if multiple available) */}
                {availableTemplates.length > 1 && (
                  <div className="ml-auto flex items-center rounded-lg border border-slate-200 overflow-hidden">
                    {availableTemplates.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => handleTemplateChange(t.id)}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          selectedTemplate === t.id
                            ? "bg-teal-50 text-teal-700 border-teal-200"
                            : "bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                        } ${
                          availableTemplates.indexOf(t) > 0
                            ? "border-l border-slate-200"
                            : ""
                        }`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Letter content — rich text editor */}
              <LetterEditor
                content={assembledLetter}
                editable={panelState === "editing"}
                onUpdate={(html) => setEditedLetter(html)}
                editorRef={editorRef}
              />

              {/* Recommendations from Claude */}
              {recommendations.length > 0 && (
                <div className="px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider mb-1.5">
                    Suggestions to Strengthen
                  </p>
                  <ul className="space-y-1">
                    {recommendations.map((rec, i) => (
                      <li
                        key={i}
                        className="text-xs text-amber-700 flex gap-1.5"
                      >
                        <span className="text-amber-400 flex-shrink-0 mt-0.5">
                          •
                        </span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Privacy notice */}
              <p className="text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
                <svg
                  className="w-3 h-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
                Patient details stay on your device — only the clinical argument
                is stored
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
